import React, { useEffect, useRef, useState } from 'react';
import { UserPool } from './auth-config';
import { AuthenticationDetails, CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Box, Button, Container, CssBaseline, FormControl, LinearProgress, Paper, SelectChangeEvent, TextField, ThemeProvider, Typography, createTheme } from '@mui/material';
import Header from './cognitoHeader';
import { Link, useNavigate } from "react-router-dom";
import ForterCaptcha from './captcha/index';
import { motion } from 'framer-motion';
import { grey } from '@mui/material/colors';
import AuthFlowSelect from './AuthFlowSelect';
import { OwnID, WidgetType } from '@ownid/react';
import { EmailInput, PasswordInput } from './InputFields';


const theme = createTheme();

type SupportedChallenges = "captcha" | "location";

function Login() {
    const [email, setEmail] = useState('');
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
    const [challengeResponse, setChallengeResponse] = useState<any>(null);
    const [showOwnID, setShowOwnID] = useState<boolean>(true);
    const emailField = useRef(null);
    const passwordField = useRef(null);

    const handleLogin = async () => {
        setLoading(true);
        const user = new CognitoUser({
            Username: email,
            Pool: UserPool,
        });

        setCognitoUser(user);

        const authDetails = new AuthenticationDetails({
            Username: email,
            Password: password,
        });
        user.setAuthenticationFlowType('CUSTOM_AUTH');
        user.authenticateUser(authDetails, {
            onSuccess: (result) => {
                setLoading(false);
                console.log('Login successful:', result);
                setMessage('Login successful! Redirecting...');
                setTimeout(() => {
                    navigate('/shop');
                }, 3000);
            },
            onFailure: (err) => {
                setLoading(false);
                console.error('Login failed:', err);
                setMessage(`Login failed: ${err.message}`);
            },
            customChallenge: (response) => {
                setLoading(false);
                if (response.captchaRequired) {
                    setMessage("Please solve the challenge to continue.");
                    setShowCaptcha(true);
                }

                if (response.locationRequired) {
                    navigator.permissions.query({ name: 'geolocation' }).then(result => {
                        if (result.state === 'granted') {
                            requestLocation();
                        } else {
                            setMessage("We have detected suspicious activity from your device. Please enable location tracking to continue.");
                            requestLocation();
                        }
                    });
                }
            },
        });
    };

    function handleOwnIDLogin(data: any) {
        try {
            const { AccessToken, IdToken, RefreshToken } = data;    
            const idToken = new CognitoIdToken({ IdToken });
            const accessToken = new CognitoAccessToken({ AccessToken });
            const refreshToken = new CognitoRefreshToken({ RefreshToken });

            const sessionData = {
                IdToken: idToken,
                AccessToken: accessToken,
                RefreshToken: refreshToken
            };
            const userSession = new CognitoUserSession(sessionData);
    
            const payload = idToken.decodePayload();
            const username = payload['cognito:username'] || payload.sub;
            const userData = {
                Username: username,
                Pool: UserPool
            };
            const cognitoUser = new CognitoUser(userData);
    
            // Set the session for this user
            cognitoUser.setSignInUserSession(userSession);
    
            console.log('User successfully logged in');
            navigate('/shop');
        } catch (error) {
            console.log('Error setting up user session:', error);
        }
    }

    function handleFlowChange(event: SelectChangeEvent<any>): void {
        const credentials = event.target.value;
        setEmail(credentials.email);
        setPassword(credentials.password);
    }

    function handleCustomChallenge(response: string, challengeName: SupportedChallenges, successMsg: string, errorMsg: string) {
        if (cognitoUser) {
            cognitoUser.sendCustomChallengeAnswer(response, {
                onSuccess: (result) => {
                    setLoading(false);
                    console.log('Challenge answer successful:', result);
                    setMessage(successMsg);
                    setTimeout(() => {
                        navigate('/shop');
                    }, 3000)
                },
                onFailure: (err) => {
                    setLoading(false);
                    console.error('Challenge answer failed:', err);
                    setMessage(errorMsg);
                },
            }, { challengeName });
        }
    }

    function requestLocation() {
        setLoading(true);
        const options = { enableHighAccuracy: true, maximumAge: 0, timeout: 60000 }
        function success(pos: GeolocationPosition) {
            const crd = pos.coords;
            console.log("Your current position is:");
            console.log(`Latitude : ${crd.latitude}`);
            console.log(`Longitude: ${crd.longitude}`);
            console.log(`More or less ${crd.accuracy} meters.`);
            setChallengeResponse({ crd, challengeName: "location" });
        }
        function error(err: any) {
            console.warn(`ERROR(${err.code}): ${err.message}`);
            setChallengeResponse(JSON.stringify({ crd: null, challengeName: "location" }));
        }
        navigator.geolocation.getCurrentPosition(success, error, options);
    }

    useEffect(() => {
        if (challengeResponse) {
            handleCustomChallenge(JSON.stringify(challengeResponse), challengeResponse.challengeName, "Success! Redirecting... ", "Location tracking failed. Please try again later.");
        }
    }, [challengeResponse]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ownIDParam = urlParams.get('pk');
        setShowOwnID(!(ownIDParam === 'false'));
      }, []);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="lg">
                <Header title="" sections={[]} />
                <Box component="img" sx={{
                    position: {
                        "md": 'absolute'
                    },
                }} src="/RocketElectronics.svg" />
                <Container maxWidth="sm">
                    <main>
                        <AuthFlowSelect formType="login" handleFlowChange={handleFlowChange} />
                        <Paper elevation={4} sx={{
                            paddingLeft: {
                                xs: "12px",
                                md: "48px",
                            },
                            paddingRight: {
                                xs: "12px",
                                md: "48px",
                            },
                            marginTop: '50px',
                            borderRadius: '20px',
                            textAlign: 'center',
                            background: 'linear-gradient(to top, #f9f9f9, #ffffff)',
                        }}>
                            <Box display="flex" flexDirection="column" justifyContent="start" alignItems="start">
                                <h2 style={{
                                    marginBottom: '10px',
                                }}>Welcome back!</h2>
                                <Typography style={{
                                    marginBottom: '40px',
                                    fontWeight: 400,
                                }} color="gray">Please sign in to continue.</Typography>
                            </Box>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {showCaptcha ? (
                                        <ForterCaptcha sitekey="Omri123" onChange={(token: string) => {
                                            setLoading(true);
                                            handleCustomChallenge(token, "captcha", "Success! Redirecting to login...", "Captcha verification failed. Please try again.");
                                        }} />
                                    ) : (
                                        <FormControl fullWidth>
                                            <EmailInput ref={emailField} value={email} onChange={(e) => setEmail(e.target.value)} />
                                            <PasswordInput ref={passwordField} value={password} onChange={(e) => setPassword(e.target.value)} />
                                            {showOwnID && <OwnID
                                                type={WidgetType.Login}
                                                passwordField={passwordField}
                                                loginIdField={emailField}
                                                onLogin={handleOwnIDLogin}
                                                onError={(e) => console.error(`OwnID err: ${e}`)}
                                            />}
                                            <Button
                                                style={{
                                                    marginTop: '12px',
                                                    height: "48px",
                                                    backgroundColor: loading ? grey[300] : '#333333',
                                                    textTransform: "none",
                                                    borderRadius: "10px",
                                                    color: loading ? grey[500] : 'white',
                                                }}
                                                fullWidth
                                                variant="contained"
                                                disabled={loading}
                                                onClick={handleLogin}
                                            >
                                                <b>Login</b>
                                            </Button>
                                        </FormControl>
                                    )}
                                    {loading && <LinearProgress sx={{
                                        width: '100%',
                                        marginTop: '24px',
                                        marginBottom: '12px',
                                        border: '1px solid #f0f0f0',
                                        backgroundColor: 'white',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: 'gray'
                                        }
                                    }} />}
                                    <Typography style={{
                                        marginTop: '24px',
                                        marginBottom: '12px',
                                        color: 'gray',
                                        fontSize: '12px',
                                    }}>Don't have an account? <Link style={{ color: 'inherit', textDecoration: 'underline' }} to="/sign-up">Sign up</Link></Typography>
                                    <p>{message}</p>
                                </Box>
                            </motion.div>
                        </Paper>
                    </main>
                </Container>
            </Container>
        </ThemeProvider>
    );
}

export default Login;
